// Input hardening for every text field in the app.
//
// Users paste email addresses and names out of Outlook, Teams, and PDFs, and
// the paste carries invisible baggage: zero-width spaces, soft hyphens, bidi
// marks, non-breaking spaces, and plain leading/trailing whitespace. The
// validators (antd's email rule, Cognito) reject these with messages that make
// no sense to the person looking at a correct-looking address.
//
// Rather than touching each of the ~36 form files, this installs document-level
// listeners that run BEFORE React's own root listeners (capture phase on
// `document` precedes capture on `#root`):
//   - `input`   : strip invisible characters as they arrive (paste or IME), and
//                 re-dispatch so React only ever sees the clean value
//   - `focusout`: trim leading/trailing whitespace when the field is left
//   - `keydown` : trim before an Enter key reaches onPressEnter / submit
//   - `submit`  : trim every text field in the form being submitted
//
// Only text-like inputs are touched (text/email/search/tel/url and textarea).
// Passwords are never altered. Add `data-keep-whitespace` to an element or any
// ancestor to opt a field out.
//
// A copy of this logic lives in bam-mp/src/utils/inputSanitizer.web.ts for the
// mp web build. Keep the two in step.

// Zero-width and formatting characters that never belong in a form value:
// soft hyphen, Mongolian vowel separator, ZWSP/ZWNJ/ZWJ/LRM/RLM, bidi
// embeddings and isolates, word joiner and invisible operators, BOM.
const INVISIBLE_RE = /[­᠎​-‏‪-‮⁠-⁤⁦-⁩﻿]/g
// Non-breaking space variants that look like a space but fail `\s`-free
// validators once they sit inside a value. Ends are handled by trim().
const ODD_SPACE_RE = /[   ]/g

/** Remove invisible characters and normalise exotic spaces; keeps the ends. */
export function stripInvisible(value) {
  if (typeof value !== 'string') return value
  return value.replace(INVISIBLE_RE, '').replace(ODD_SPACE_RE, ' ')
}

/** stripInvisible + trim: the value a validator should see. */
export function sanitizeText(value) {
  if (typeof value !== 'string') return value
  return stripInvisible(value).trim()
}

const TEXT_TYPES = ['text', 'email', 'search', 'tel', 'url']

function isSanitizable(el) {
  if (!el || typeof el.value !== 'string') return false
  const tag = el.tagName
  if (tag !== 'INPUT' && tag !== 'TEXTAREA') return false
  if (el.disabled || el.readOnly) return false
  if (
    tag === 'INPUT' &&
    !TEXT_TYPES.includes((el.type || 'text').toLowerCase())
  )
    return false
  if (el.closest && el.closest('[data-keep-whitespace]')) return false
  return true
}

// React tracks the last value it set on the node; writing through the
// prototype setter (not the instance) is what makes a dispatched `input`
// event register as a real change.
function setNativeValue(el, next) {
  const proto =
    el.tagName === 'TEXTAREA'
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype
  const desc = Object.getOwnPropertyDescriptor(proto, 'value')
  if (desc && desc.set) desc.set.call(el, next)
  else el.value = next
}

function replaceValue(el, next, caret) {
  setNativeValue(el, next)
  if (typeof caret === 'number') {
    try {
      el.setSelectionRange(caret, caret)
    } catch (e) {
      // type=email inputs refuse selection APIs; the caret lands at the end
    }
  }
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

function onInput(e) {
  const el = e.target
  if (e.isComposing || !isSanitizable(el)) return
  const value = el.value
  const clean = stripInvisible(value)
  if (clean === value) return
  // Swallow the dirty event; the re-dispatch below carries the clean value.
  e.stopImmediatePropagation()
  let caret = null
  try {
    const start = el.selectionStart
    if (typeof start === 'number')
      caret = stripInvisible(value.slice(0, start)).length
  } catch (err) {
    caret = null
  }
  replaceValue(el, clean, caret)
}

function trimElement(el) {
  if (!isSanitizable(el)) return
  const value = el.value
  const clean = sanitizeText(value)
  if (clean !== value) replaceValue(el, clean, null)
}

function onFocusOut(e) {
  trimElement(e.target)
}

function onKeyDown(e) {
  if (e.key !== 'Enter' || e.isComposing) return
  const el = e.target
  // Enter in a textarea is a newline, not a submit
  if (el && el.tagName === 'INPUT') trimElement(el)
}

function onSubmit(e) {
  const form = e.target
  if (!form || !form.elements) return
  Array.prototype.forEach.call(form.elements, trimElement)
}

const FLAG = '__bamInputSanitizer'

/**
 * Install the listeners once per document. Returns an uninstall function.
 */
export function installInputSanitizer(doc) {
  const target = doc || (typeof document !== 'undefined' ? document : null)
  if (!target || target[FLAG]) return () => {}
  target[FLAG] = true
  target.addEventListener('input', onInput, true)
  target.addEventListener('focusout', onFocusOut, true)
  target.addEventListener('keydown', onKeyDown, true)
  target.addEventListener('submit', onSubmit, true)
  return () => {
    target.removeEventListener('input', onInput, true)
    target.removeEventListener('focusout', onFocusOut, true)
    target.removeEventListener('keydown', onKeyDown, true)
    target.removeEventListener('submit', onSubmit, true)
    delete target[FLAG]
  }
}
